import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToasterService } from 'angular2-toaster';
import axios from 'axios';
import { DEVICE_ACTIONS, ENDPOINTS } from '../app.config';
import Utils from '../app.utilities';
import { CompanyService } from '../company.service';
import { UserInfoService } from '../shared/user-info.service';
import { TranslateService } from '@ngx-translate/core';

const utilities = new Utils();

@Component({
  selector: 'app-device-edit',
  templateUrl: './device-edit.component.html',
  styleUrls: ['./device-edit.component.css'],
  // Is already provided in ../app.module.ts
  //  but the UserInfoService, in the constructor, needs access to ActivatedRoute.params
  //  https://stackoverflow.com/questions/39977962/angular-2-0-2-activatedroute-is-empty-in-a-service
  providers: [UserInfoService]
})
export class DeviceEditComponent implements OnInit {
  channelsList = [];
  selectedChannels = [];
  dropdownSettings = {};
  companyId: string;
  deviceId: any;
  companyName: string;
  userRole: string;
  currentPage: number;
  totalPages: number;
  devicesTotal: number;
  devicesPerPage: number;
  gettingDevicesInProgress: boolean;
  sub: any;
  devices: any;
  chosenDevices: any;
  actions: any;
  route: ActivatedRoute;
  companyService: CompanyService;
  toaster: ToasterService;
  businesOwnerName : string;
  businessOwnerIdLocalStorage = localStorage.getItem('businessOwnerId');
  statusCheckInterval;

  // Messages
  no_device_selected: string;
  command_sent_successfully: string;
  error_sending_command: string;

  constructor(
    route: ActivatedRoute,
    companyService: CompanyService,
    toaster: ToasterService,
    private userInfoService : UserInfoService,
    private translate: TranslateService
  ) {
    this.route = route;
    this.companyService = companyService;
    this.toaster = toaster;
  }

  ngOnInit() {
    this.actions = DEVICE_ACTIONS;
    this.chosenDevices = [];
    this.devices = [];
    this.currentPage = 1;
    this.gettingDevicesInProgress = false;
    // Translations
    this.translate.stream('SHARED.SelectBox.select_channels').subscribe((res: string) => {
      this.dropdownSettings = {
        singleSelection: false,
        enableCheckAll: false,
        text: res,
        selectAllText: 'Select All',
        unSelectAllText: 'Remove All',
        enableSearchFilter: false,
        classes: 'item-separation c-token',
      };
    });
    this.sub = this.route.params.subscribe(params => {
      this.companyId = params.companyId;
      this.companyService.setCompanyId(this.companyId);
      this.deviceId = params.deviceId || null;
      this.getCompanyInfo();
      this.getUserInfo();
      this.getChannels();
      this.initDevices();
    });
    // in order to recheck the status of each device
   this.statusCheckInterval = setInterval( () => {
       this.getChannels();
       this.initDevices();
   }, 10000);

    // Messages translations
    this.translate.stream('SHARED.Messages.no_device_selected').subscribe((res: string) => {
      this.no_device_selected = res;
    });
    this.translate.stream('SHARED.Messages.command_sent_successfully').subscribe((res: string) => {
      this.command_sent_successfully = res;
    });
    this.translate.stream('SHARED.Messages.error_sending_command').subscribe((res: string) => {
      this.error_sending_command = res;
    });
  }

  ngOnDestory(){
    clearInterval(this.statusCheckInterval);
  }

  initDevices() {
    if (this.deviceId) {
      this.getOnlyOneDevice();
    } else {
      this.getDevices();
    }
  }

  getOnlyOneDevice() {
    const _self = this;
    axios.get(ENDPOINTS.DEVICES.replace(':companyId', this.companyId) + '/' + this.deviceId).then(res => {
      _self.devices = [res.data];
    });
  }

  getChannels() {
    const _self = this;
    axios
      .get(ENDPOINTS.GET_CHANNELS.replace(':companyId', this.companyId))
      .then(response => {
        _self.channelsList = _self.generateChannelsList(response.data);
      });
  }

  onItemSelect(channel) {
    this.getDevices();
  }

  onItemDeselect(channel) {
    this.getDevices();
  }

  generateChannelsList(channels) {
    const channelsList = [];
    for (const item of channels) {
      channelsList.push({
        id: item.id,
        itemName: item.name,
      });
    }
    return channelsList;
  }

  getChannelsIds() {
    const arr = [];
    for (const channel of this.selectedChannels) {
      arr.push(channel.id);
    }
    return arr;
  }

  getDevices() {
    const _self = this;
    if (_self.gettingDevicesInProgress) {
      return;
    }
    let channelIds = [];
    if (_self.selectedChannels.length) {
      channelIds = this.getChannelsIds();
    }
    _self.gettingDevicesInProgress = true;
    let url = channelIds.length
      ? ENDPOINTS.DEVICES.replace(':companyId', this.companyId) +
          '?confirmed=true&channel_ids=' +
          channelIds.join(',')
      : ENDPOINTS.DEVICES.replace(':companyId', this.companyId) +
          '?confirmed=true';
    axios.get(url).then(
      response => {
        _self.devicesTotal = parseInt(response.headers['total'], 10);
        _self.devicesPerPage = parseInt(response.headers['per-page'], 10);
        _self.totalPages = Math.floor(
          _self.devicesTotal % _self.devicesPerPage,
        ) === 0
          ? _self.devicesTotal / _self.devicesPerPage
          : Math.floor(_self.devicesTotal / _self.devicesPerPage) + 1;
        url = channelIds.length
          ? ENDPOINTS.DEVICES.replace(':companyId', this.companyId) +
              '?confirmed=true' +
              '&page=' +
              _self.currentPage +
              '&per_page=' +
              _self.devicesPerPage +
              '&channel_ids=' +
              channelIds.join(',')
          : ENDPOINTS.DEVICES.replace(':companyId', this.companyId) +
              '?confirmed=true' +
              '&page=' +
              _self.currentPage +
              '&per_page=' +
              _self.devicesPerPage;
        axios.get(url).then(pagedResponse => {
          _self.devices = pagedResponse.data;
          _self.gettingDevicesInProgress = false;
          _self.setCheckedField();
        });
      },
      error => {
        _self.gettingDevicesInProgress = false;
      },
    );
  }

  resetCheckedDevices() {
    this.chosenDevices = [];
    for (const device of this.devices) {
      device['checked'] = false;
    }
  }

  setCheckedField() {
    for (const device of this.devices) {
      device.checked = this.chosenDevices.includes(device.id);
    }
  }

  toggleDeviceChosen(deviceId) {
    if (this.chosenDevices.includes(deviceId)) {
      this.chosenDevices.splice(this.chosenDevices.indexOf(deviceId), 1);
    } else {
      this.chosenDevices.push(deviceId);
    }
  }

  loadDevices(page, isDisabled) {
    if (!isDisabled && !this.gettingDevicesInProgress) {
      this.currentPage = page;
      this.getDevices();
    }
  }

  generatePages() {
    if (!this.totalPages) {
      return [];
    }
    const arr = [];
    for (let i = 0; i < this.totalPages; i++) {
      arr.push(i + 1);
    }
    return arr;
  }

  getCompanyInfo() {
    const _self = this;
    axios
      .get(ENDPOINTS.GET_COMPANIES + '/' + _self.companyId)
      .then(response => {
        _self.companyName = response.data.name;
      });
  }

  getUserInfo() {
    const _self = this;

    this.userInfoService.getInformations()
    .subscribe( info => {
      _self.userRole         = info.userRole;
      _self.companyName      = info.companyName;
      _self.businesOwnerName = info.businesOwnerName;
    });
  }

  sendDeviceAction(action) {
    if (this.chosenDevices.length === 0) {
      return this.toaster.pop('error', this.no_device_selected);
    }
    const _self = this;
    axios
      .post(
        ENDPOINTS.DEVICE_ACTION.replace(':companyId', this.companyId) +
          '?command=' +
          action +
          '&ids=' +
          this.chosenDevices,
      )
      .then(
        response => {
          _self.resetCheckedDevices();
          _self.loadDevices(_self.currentPage, false);
          _self.toaster.pop('success', this.command_sent_successfully);
        },
        error => {
          _self.toaster.pop('error', this.error_sending_command);
        },
      );
  }
}



// WEBPACK FOOTER //
// /Users/mihagresak/Documents/git-projects/vintegras-angular-new-clean/src/app/device-edit/device-edit.component.ts